import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Html5QrcodeScanner, Html5QrcodeScanType } from 'html5-qrcode';
import { useModalBackHandler } from '../hooks/useModalBackHandler';
import AnimatedCloseButton from './AnimatedCloseButton';

interface BarcodeScannerProps {
  isOpen: boolean;
  onClose: () => void;
  onScan: (result: string) => void;
  onError?: (error: string) => void;
  onAutoCheck?: () => void; // Otomatik kontrol için
}

export default function BarcodeScanner({ isOpen, onClose, onScan, onError, onAutoCheck }: BarcodeScannerProps) {
  const scannerRef = useRef<Html5QrcodeScanner | null>(null);
  const [error, setError] = useState<string>('');
  const [isLocalized, setIsLocalized] = useState<boolean>(false);
  const scannerElementId = 'html5qr-code-scanner';

  // Modal kapatma handler'ı
  const handleClose = useCallback(() => {
    if (scannerRef.current) {
      try {
        scannerRef.current.clear();
        scannerRef.current = null;
      } catch (err) {
        console.warn('Scanner temizleme hatası:', err);
      }
    }
    setError('');
    setIsLocalized(false);
    onClose();
  }, [onClose]);

  // Modal geri düğmesi handler
  useModalBackHandler({ 
    isOpen, 
    onClose: handleClose, 
    enabled: true
  });

  // Html5QrcodeScanner başlatma - Dokümantasyona göre düzeltildi
  const startScanner = () => {
    try {
      setError('');

      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
        supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA],
        // Arka kamerayı tercih et
        videoConstraints: {
          facingMode: "environment"
        },
        rememberLastUsedCamera: true,
        showTorchButtonIfSupported: true
      };

      const scanner = new Html5QrcodeScanner(scannerElementId, config, /* verbose= */ false);
      scannerRef.current = scanner;

      scanner.render(
        // Success callback
        (decodedText, decodedResult) => {
          onScan(decodedText);
          handleClose();
          // Barkod okunduktan sonra otomatik kontrol et
          if (onAutoCheck) {
            setTimeout(() => {
              onAutoCheck();
            }, 500); // Kısa gecikme ile kontrol butonuna tıkla
          }
        },
        // Error callback - Dokümantasyona göre: "handle scan failure, usually better to ignore and keep scanning"
        (errorMessage) => {
          // Sadece gerçek hataları handle et, NotFoundException normal
          if (errorMessage.includes('NotAllowedError') || 
              errorMessage.includes('Permission denied')) {
            setError('Kamera erişimi reddedildi. Lütfen tarayıcı ayarlarından kamera iznini verin.');
          } else if (errorMessage.includes('NotFoundError') || 
                     errorMessage.includes('NotReadableError')) {
            setError('Kamera bulunamadı veya kullanılamıyor.');
          }
          // NotFoundException ve diğer tarama hataları ignore edilir (dokümantasyona göre)
        }
      );

      // Scanner render olduktan sonra kamera izin butonunu Türkçeleştir
      setTimeout(() => {
        const permissionButton = document.getElementById('html5-qrcode-button-camera-permission');
        if (permissionButton) {
          permissionButton.textContent = 'Kamera Erişimi İste';
        }
        // Türkçeleştirme tamamlandı, scanner'ı göster
        setIsLocalized(true);
      }, 1000);
      
    } catch (err) {
      console.error('Scanner başlatma hatası:', err);
      const errorMessage = err instanceof Error ? err.message : 'Tarayıcı başlatılamadı.';
      setError(errorMessage);
      onError?.(errorMessage);
    }
  };

  // Scanner durdurma
  const stopScanner = () => {
    if (scannerRef.current) {
      try {
        scannerRef.current.clear();
        scannerRef.current = null;
      } catch (err) {
        console.warn('Scanner temizleme hatası:', err);
      }
    }
  };





  // Backdrop tıklama handler'ı
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Modal açıldığında scanner'ı başlat
  useEffect(() => {
    if (isOpen) {
      // Kısa bir gecikme ile scanner'ı başlat (DOM'un hazır olması için)
      setTimeout(() => {
        startScanner();
      }, 300);
    } else {
      stopScanner();
      setError('');
      setIsLocalized(false);
    }

    return () => {
      stopScanner();
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 overflow-y-auto bg-black/20 dark:bg-black/40 backdrop-blur-sm"
          onClick={handleBackdropClick}
        >
          {/* Modal */}
          <div className="flex min-h-full items-center justify-center px-2 py-4 text-center sm:p-0">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative transform transition-transform overflow-hidden rounded-xl backdrop-blur-xs bg-gradient-to-br from-white/90 via-white/88 to-white/85 dark:from-gray-700/85 dark:via-gray-750/83 dark:to-gray-800/85 border border-white/50 dark:border-gray-600/50 text-left shadow-2xl shadow-black/10 dark:shadow-black/30 w-full mx-2 sm:mx-0 sm:my-8 sm:w-full sm:max-w-lg"
              style={{ height: '512px' }}
              role="dialog"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="border-b border-gray-300 dark:border-gray-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Barkod Okuyucu
                  </h3>
                  <AnimatedCloseButton onClick={handleClose} />
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {error ? (
                  <div className="text-center">
                    <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Kamera Hatası
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                      {error}
                    </p>
                    <button
                      onClick={startScanner}
                      className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
                    >
                      Tekrar Dene
                    </button>
                  </div>
                ) : (
                  <div>
                    {!isLocalized && (
                      <div className="flex items-center justify-center" style={{ minHeight: '400px' }}>
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                          <p className="text-gray-600 dark:text-gray-400">Barkod okuyucu hazırlanıyor...</p>
                        </div>
                      </div>
                    )}
                    <div 
                      id={scannerElementId} 
                      className={`w-full ${isLocalized ? 'block' : 'hidden'}`} 
                      style={{ minHeight: '400px' }}
                    ></div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}